import { MouseEvent, useEffect, useRef, useState } from 'react'
import { Typography, Card, Box, Stack, Button } from '@mui/material'
import {
  CardItem,
  TypDescription,
  TypCharacteristics,
} from './style'
import { useNavigate } from 'react-router-dom'
import { StyledBadge } from '../../../ui-kit'
import { routesProfile } from '../../../../consts'
import { USER_STATUS_OPTIONS } from '../../../../consts/userStatus'
import modalEmitter from '../../../ModalComponent/modalEmitter'
import {
  InviteProjectModalBody,
  InviteTeamModalBody,
} from '../../../Modals'
import type { TUserListItem } from '../../../../api/types/users'
import { profileApi } from '../../../../api/profile'
import { UserAvatar } from '../../../common/UserAvatar'
import { useFollowUserMutation, useUnfollowUserMutation } from '../../../../api/users'
import { toast } from 'react-toastify'
import { useSettledToggleNetwork } from '../../../../hooks/useSettledToggleNetwork'

interface DesktopCardUserProps {
  user: TUserListItem
}

export const DesktopCard = ({ user }: DesktopCardUserProps) => {
  const navigate = useNavigate()
  const { data: me } = profileApi.useGetProfileQuery()
  const [followUser, { isLoading: isFollowLoading }] = useFollowUserMutation()
  const [unfollowUser, { isLoading: isUnfollowLoading }] = useUnfollowUserMutation()
  const [isFollowed, setIsFollowed] = useState(user.is_followed === true)
  const followSettle = useSettledToggleNetwork()
  const followBurstRef = useRef<boolean | null>(null)

  useEffect(() => {
    setIsFollowed(user.is_followed === true)
  }, [user.id, user.is_followed])

  const competencies = user.competencies.map((item) => item.name)
  const resources = user.resources.map((item) => item.name)
  const participation = user.participation.map((item) => item.name)
  const statusOption = USER_STATUS_OPTIONS.find((option) => String(option.value) === user.status)
  const statusLabel = statusOption?.label ?? user.status

  const navigateToUser = () => {
    navigate(`/${routesProfile.USERS.path}/${user.slug}`)
  }
  const handleEdit = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    navigate(`/${routesProfile.PROFILE.path}`)
  };
  const isFollowActionLoading = isFollowLoading || isUnfollowLoading
  const followActionLabel = isFollowActionLoading
    ? 'Загрузка...'
    : (isFollowed ? 'Отписаться' : 'Подписаться')

  const handleFollowClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (isFollowActionLoading) {
      return
    }

    if (followBurstRef.current === null) {
      followBurstRef.current = isFollowed
    }
    const next = !isFollowed
    setIsFollowed(next)

    followSettle.schedule({
      desired: next,
      commit: async (desired) => {
        followBurstRef.current = null
        if (desired) {
          await followUser({ userId: user.id }).unwrap()
          toast.success('Вы подписались на пользователя')
        } else {
          await unfollowUser({ userId: user.id }).unwrap()
          toast.success('Вы отписались от пользователя')
        }
      },
      onSkip: () => {
        followBurstRef.current = null
      },
      onError: (baseline) => {
        followBurstRef.current = null
        setIsFollowed(baseline)
        toast.error('Не удалось изменить подписку')
      },
    })
  }

  const handleInviteProjectClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    modalEmitter.emitOpenModal({
      dialog: {
        title: "Пригласить в проект",
        body: <InviteProjectModalBody userId={user.id} />,
      },
      closeAtion: { visible: false },
      confirmAtion: { visible: false },
    })
  }

  const locationPart = user.location !== null && user.location !== '' ? `${user.location}, ` : ''

  const handleInviteTeamClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    modalEmitter.emitOpenModal({
      dialog: {
        title: "Пригласить в команду",
        body: <InviteTeamModalBody userId={user.id} />,
      },
      closeAtion: { visible: false },
      confirmAtion: { visible: false },
    })
  }

  return (
    <Card
      variant='primaryCard'
      sx={CardItem}
      onClick={navigateToUser}
    >
      <Stack direction='row' gap={3}>
        <Box>
          <StyledBadge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
            invisible={!user.is_online}
          >
            <UserAvatar src={user.avatar} size={100} />
          </StyledBadge>
        </Box>

        <Stack gap={1} minWidth={0} width="100%">
          <Box>
            <Stack direction='row' alignItems='center'>
              <Typography variant='h3' flex={1} minWidth={0} noWrap>{user.shortname}</Typography>
              {me?.id === user.id ? (
                <Button variant="follow" sx={{ ml: 1 }} onClick={handleEdit}>Редактировать</Button>
              ) : (
                <Button
                  variant="follow"
                  sx={{ ml: 1 }}
                  onClick={handleFollowClick}
                  disabled={isFollowActionLoading}
                >
                  {followActionLabel}
                </Button>
              )}
            </Stack>
            <Typography variant='h4' sx={TypDescription} noWrap>{locationPart}{user.age} лет</Typography>
            <Typography variant='h4' sx={TypDescription} noWrap>{statusLabel}</Typography>
          </Box>
          <Typography
            variant='body1'
            sx={TypCharacteristics}
          >
            <Box component='span'>Компетенции: </Box>{competencies.join("; ")}
          </Typography>
          <Typography
            variant='body1'
            sx={TypCharacteristics}
          >
            <Box component='span'>Доступные ресурсы: </Box>{resources.join("; ")}
          </Typography>
          <Typography
            variant='body1'
            sx={TypCharacteristics}
          >
            <Box component='span'>Условия участия: </Box>{participation.join("; ")}
          </Typography>
          {me?.id !== user.id && (
            <Stack direction="row" alignItems="center" gap={1.5}>
              <Button variant="action" onClick={handleInviteProjectClick}>Пригласить в проект</Button>
              <Button variant="action" onClick={handleInviteTeamClick}>Пригласить в команду</Button>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Card>
  )
}
